import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Sun, Moon } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import VentaraMark from "@/components/common/VentaraMark";
import VentaraWordmark from "@/components/common/VentaraWordmark";

// First 6 render in the desktop nav; the rest (Careers, Research, Studio,
// Design Language) show only in the mobile menu — they already live in the
// footer for desktop discoverability. Careers is footer/mobile-only for
// now since we're not actively hiring; move it back into the first 6 when
// that changes.
const links = [
    { to: "/solutions", key: "solutions" },
    { to: "/industries", key: "industries" },
    { to: "/products", key: "products" },
    { to: "/technology", key: "technology" },
    { to: "/insights", key: "insights" },
    { to: "/about", key: "about" },
    { to: "/careers", key: "careers" },
    { to: "/research", key: "research" },
    { to: "/studio", key: "studio" },
    { to: "/vdl", key: "vdl" },
];

export default function Nav() {
    const { t } = useTranslation();
    const { theme, toggle } = useTheme();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Home's hero is a fixed dark-navy brand canvas regardless of theme, and
    // the nav floats transparently over it until scrolled. The nav's normal
    // text color follows the site theme, which goes near-black in light mode
    // — invisible on that hero. Once scrolled, the nav gets its own solid
    // `bg-background` and theme-reactive text is correct again.
    const overDarkHero = pathname === "/" && !scrolled;

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
                scrolled ? "backdrop-blur-xl bg-background/70 border-b border-border" : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
                    <VentaraMark size={26} className="transition-transform duration-500 group-hover:rotate-[8deg]" />
                    <VentaraWordmark height={11} className={overDarkHero ? "text-white" : ""} />
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {links.slice(0, 6).map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.key}`}
                            className={({ isActive }) =>
                                `text-sm font-light tracking-wide transition-colors ${
                                    overDarkHero
                                        ? isActive ? "text-white" : "text-white/60 hover:text-white"
                                        : isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                                }`
                            }
                        >
                            {t(`nav.${l.key}`)}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        onClick={toggle}
                        data-testid="nav-theme-toggle"
                        aria-label="Toggle theme"
                        className={`h-9 w-9 rounded-full grid place-items-center transition-colors ${
                            overDarkHero ? "text-white hover:bg-white/10" : "hover:bg-muted"
                        }`}
                    >
                        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>
                    <Link to="/contact" className="hidden sm:block">
                        <Button
                            data-testid="nav-contact-btn"
                            className="rounded-full h-9 px-5 text-sm font-normal bg-foreground text-background hover:bg-foreground/90"
                        >
                            {t("nav.contact")}
                        </Button>
                    </Link>
                    <button
                        className={`lg:hidden h-9 w-9 grid place-items-center rounded-full ${
                            overDarkHero ? "text-white hover:bg-white/10" : "hover:bg-muted"
                        }`}
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-toggle"
                        aria-label="Open menu"
                    >
                        {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-border bg-background" data-testid="nav-mobile-menu">
                    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-6 grid grid-cols-2 gap-y-4 gap-x-8">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-mobile-link-${l.key}`}
                                className="text-sm text-muted-foreground hover:text-foreground"
                            >
                                {t(`nav.${l.key}`)}
                            </NavLink>
                        ))}
                        <NavLink
                            to="/contact"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-link-contact"
                            className="text-sm text-foreground font-medium"
                        >
                            Contact →
                        </NavLink>
                    </div>
                </div>
            )}
        </header>
    );
}
